
import React from 'react';
import { Navigate, useLocation, Outlet } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface AdminRouteProps {
  children?: React.ReactNode;
}

const AdminRoute = ({ children }: AdminRouteProps) => {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['admin-check', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('profiles')
        .select('user_type')
        .eq('id', user.id)
        .single();
      return data;
    },
    enabled: !!user,
  });

  if (authLoading || profileLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    toast.error('Authentication required.');
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }

  if (!['admin', 'manager'].includes(profile?.user_type || '')) {
    toast.error('Admin access required.');
    return <Navigate to="/" replace />;
  }

  return children ?? <Outlet />;
};

export default AdminRoute;
